import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import type { ReactNode } from "react";

export type AlertType = "warn" | "info" | "ok";

export interface AlertRowProps {
  type: AlertType;
  /** Phrase principale en gras (ex : "2 lots atteindront 36 mois sur lattes le mois prochain."). */
  title: string;
  /** Texte en suite, non gras, qui complète le titre. */
  body?: string;
  /** Métadonnée(s) sous le titre + corps : peut contenir un Tag, du texte gris, etc. */
  meta?: ReactNode;
  /** Action à droite (bouton ou lien). Le caller fournit son propre élément. */
  action?: ReactNode;
  /** Surcharge l'icône par défaut (par défaut : ⚠ warn / ℹ info / ✓ ok). */
  icon?: ReactNode;
  /** Callback global ligne (cliquable). Si action est aussi fournie, l'action
   *  intercepte avec stopPropagation côté caller. */
  onClick?: () => void;
}

const DEFAULT_ICONS: Record<AlertType, ReactNode> = {
  warn: <AlertTriangle size={16} />,
  info: <Info size={16} />,
  ok: <CheckCircle2 size={16} />,
};

/**
 * Ligne d'alerte du bloc "Vous devriez jeter un œil" / centre de notif.
 * Composition : icône colorée + titre/corps + meta + action à droite.
 */
export function AlertRow({ type, title, body, meta, action, icon, onClick }: AlertRowProps) {
  // Si onClick fourni, on rend la ligne réellement focusable au clavier ET on
  // active le cursor: pointer + hover via la classe modificatrice.
  const interactiveProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        },
      }
    : {};

  const className = onClick ? "ui-alert-row ui-alert-row--interactive" : "ui-alert-row";

  return (
    <div className={className} {...interactiveProps}>
      <div className={`ui-alert-icon ${type}`}>{icon ?? DEFAULT_ICONS[type]}</div>
      <div className="min-w-0 flex-1 leading-snug">
        <div className="text-[14px]">
          <span className="font-semibold">{title}</span>
          {body && <> {body}</>}
        </div>
        {meta && (
          <div className="mt-1.5 flex items-center gap-2 text-[12px] text-champagne-700">
            {meta}
          </div>
        )}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
